import clsx from 'clsx';
import LoadingSpinner from 'components/common/loading-spinner';
import SystemComponentsTable from 'components/systems/system-components-table';
import { SystemOptionType } from 'data/types';
import { DuctedComponentSwapHandler } from 'hooks/use-ducted-component-swap';
import { DuctedSwapLockState } from 'utils/ducted-component-swap-types';
import {
  includesKeptEquipmentEstimate,
  KEPT_EQUIPMENT_COST_NOTE
} from 'utils/ducted-system-metrics';
import { formatPrice } from 'utils/helpers';
import {
  formatAnnualOperatingCostLine,
  formatMonthlyOperatingCost
} from 'utils/operating-cost-format';
import { coverageSidesForSystem } from './coverage-sides';
import { CoverageGrid, PerformanceInYourAreaTitle } from './overview-cards';
import ColumnLoadingVeil from './column-loading-veil';
import { ComparisonSectionRowCount } from './system-column-helpers';
import SystemComparisonSummary from './system-comparison-summary';
import SystemFloorplan from './system-floorplan';
import { MatchScoreOverviewCard } from './system-overview-band';
import ZonePerformanceTable from './zone-performance-table';

type SystemColumnProps = Readonly<{
  system: SystemOptionType;
  sectionRowCount: ComparisonSectionRowCount;
  showZoningLayout: boolean;
  isRefreshPending: boolean;
  optionLoading: boolean;
  onCustomSystemSaved: (uniqueId: string) => void;
  onDuctedComponentSwap?: DuctedComponentSwapHandler;
  ductedLockState?: DuctedSwapLockState;
  aiSummary: string | undefined;
  aiSummaryLoading: boolean;
  aiSummaryError: boolean;
}>;

type SystemSummarySectionProps = Readonly<{
  system: SystemOptionType;
  aiSummary: string | undefined;
  aiSummaryLoading: boolean;
  aiSummaryError: boolean;
}>;

function SystemSummarySection({
  system,
  aiSummary,
  aiSummaryLoading,
  aiSummaryError
}: SystemSummarySectionProps) {
  const keptEquipmentEstimate = includesKeptEquipmentEstimate(
    system.ductedOperatingCost
  );
  const annualCost = system.totalAnnualCost ?? null;

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <div className='text-[10px] font-semibold uppercase tracking-wider text-gray-500'>
          <PerformanceInYourAreaTitle />
        </div>
        <div className='mt-2'>
          <CoverageGrid sides={coverageSidesForSystem(system)} bordered />
        </div>
      </div>

      {system.score && <MatchScoreOverviewCard system={system} />}

      <div className='grid grid-cols-2 gap-2'>
        <div className='rounded-lg border border-gray-200 p-3'>
          <div className='text-[10px] font-semibold uppercase tracking-wider text-gray-500'>
            Total Equipment Cost
          </div>
          <div className='mt-1 text-xl font-bold text-gray-900'>
            {formatPrice(system.price)}
          </div>
          <div className='text-xs text-gray-500'>
            (Excluding installation cost)
          </div>
        </div>
        <div className='rounded-lg border border-gray-200 p-3'>
          <div className='text-[10px] font-semibold uppercase tracking-wider text-gray-500'>
            Average Operating Cost
          </div>
          {annualCost === null ? (
            <div className='mt-1 text-xl font-bold text-gray-900'>--</div>
          ) : (
            <>
              <div className='mt-1 text-xl font-bold text-gray-900'>
                {formatMonthlyOperatingCost(annualCost)}{' '}
                <span className='text-sm text-gray-500'>/ month</span>
              </div>
              <div className='text-xs text-gray-500'>
                {formatAnnualOperatingCostLine(annualCost)}
              </div>
              {keptEquipmentEstimate && (
                <div className='text-xs text-gray-500'>
                  {KEPT_EQUIPMENT_COST_NOTE}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <SystemComparisonSummary
        summary={aiSummary}
        isLoading={aiSummaryLoading}
        hasError={aiSummaryError}
      />
    </div>
  );
}

export default function SystemColumn({
  system,
  sectionRowCount,
  showZoningLayout,
  isRefreshPending,
  optionLoading,
  onCustomSystemSaved,
  onDuctedComponentSwap,
  ductedLockState,
  aiSummary,
  aiSummaryLoading,
  aiSummaryError
}: SystemColumnProps) {
  return (
    <div
      className={clsx(
        'relative grid min-w-0 rounded-b-lg border-x border-b border-gray-200 bg-white p-4 shadow-sm [grid-template-rows:subgrid]',
        sectionRowCount === 4 ? '[grid-row:span_4]' : '[grid-row:span_3]',
        optionLoading && 'pointer-events-none'
      )}
    >
      <div className='min-w-0'>
        <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500'>
          System Summary
        </div>
        <SystemSummarySection
          system={system}
          aiSummary={aiSummary}
          aiSummaryLoading={aiSummaryLoading}
          aiSummaryError={aiSummaryError}
        />
      </div>

      {showZoningLayout ? (
        <div className='min-w-0'>
          <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500'>
            Zoning &amp; Layout
          </div>
          <SystemFloorplan system={system} />
        </div>
      ) : (
        sectionRowCount === 4 && <div className='min-w-0' />
      )}

      <div className='min-w-0'>
        <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500'>
          System Components
        </div>
        <SystemComponentsTable
          option={system}
          onCustomSystemSaved={onCustomSystemSaved}
          onDuctedComponentSwap={onDuctedComponentSwap}
          ductedLockState={ductedLockState}
        />
      </div>

      <div className='min-w-0'>
        <ZonePerformanceTable system={system} />
      </div>

      {isRefreshPending && (
        <div className='absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-b-lg bg-white/60'>
          <LoadingSpinner />
          <span className='text-sm font-medium text-gray-600'>
            Updating systems...
          </span>
        </div>
      )}
      {optionLoading && <ColumnLoadingVeil className='rounded-b-lg' />}
    </div>
  );
}
